/**
 * Dependencies
 */
import { RESTService } from '@prz-dp/services';

import app from '@/constants/application.constant';

import { PartnershipModel } from './partnership.model';

/**
 * Service `PartnershipService`.
 */
class PartnershipService extends RESTService<PartnershipModel, number> {
  /**
   * Constructor`.
   */
  constructor() {
    super(PartnershipModel);
  }

  /**
   * Get partnership.
   */
  async getPartnership({ id }: { id: number }) {
    const {
      data: result,
      errors
    }: {
      data: { partnershipProzis: PartnershipModel };
      errors: any;
    } = await this.POST(app.baseGraphqlUrl, {
      operationName: 'partnershipProzisByIdQuery',
      query: `
          query partnershipProzisByIdQuery($id: Int!) {
            partnershipProzis(id: $id) {
              id
              accountId
              canAccessFromBrowser
              description
              key
              membersAccess
              needsToAcceptTerms
              account {
                emailAccount
              }
              partnershipTeam {
                partnerTermsDocument
              }
              samAsset {
                url
              }
            }
          }
        `,
      variables: { id }
    });

    if (errors && errors.length) {
      throw errors;
    }

    const { partnershipProzis } = result || {};

    return partnershipProzis;
  }

  /**
   * Get partnerships.
   */
  async getPartnerships({
    limit = 20,
    page = 0,
    searchString = ''
  }: {
    limit: number;
    page: number;
    searchString?: string;
  }) {
    const {
      data: result,
      errors
    }: {
      data: {
        partnershipProzisList: { count: number; data: PartnershipModel[] };
      };
      errors: any;
    } = await this.POST(app.baseGraphqlUrl, {
      operationName: 'partnershipProzisListQuery',
      query: `
          query partnershipProzisListQuery(
            $limit: Int
            $page: Int
            $searchString: String
          ) {
            partnershipProzisList(
              limit: $limit
              page: $page
              searchString: $searchString
            ) {
              data {
                id
                accountId
                canAccessFromBrowser
                description
                key
                membersAccess
                needsToAcceptTerms
                account {
                  emailAccount
                }
                samAsset {
                  url
                }
              }
              count
            }
          }
        `,
      variables: { limit, page, searchString }
    });

    if (errors && errors.length) {
      throw errors;
    }

    const { partnershipProzisList = null } = result || {};
    const { count, data = [] } = partnershipProzisList || {};

    return { count, data };
  }

  /**
   * Get partnership addresses.
   */
  async getPartnershipAddresses(id: number) {
    return this.GET(`/api/private/partnerships/${id}/addresses`);
  }

  /**
   * Check if username exists.
   */
  async checkUsernameExists(username: string): Promise<boolean> {
    return this.GET(
      `/api/private/partnerships/social-network-profile-username/${username}/exists`
    );
  }

  /**
   * Accept partnership terms.
   */
  async acceptTerms(id: number) {
    const {
      data: result,
      errors
    }: {
      data: { partnerAcceptTerms: PartnershipModel };
      errors: any;
    } = await this.POST(app.baseGraphqlUrl, {
      operationName: 'partnerAcceptTermsMutation',
      query: `
          mutation partnerAcceptTermsMutation($id: Int!) {
            partnerAcceptTerms(id: $id) {
              id
            }
          }
        `,
      variables: { id }
    });

    if (errors && errors.length) {
      throw errors;
    }

    const { partnerAcceptTerms } = result || {};

    return partnerAcceptTerms;
  }
}

/**
 * Export `partnershipService`.
 */
export const partnershipService = new PartnershipService();
